import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { usePageStructure } from "@/providers/PageStructureProvider";
import { PageLayoutConfig } from "@/components/layout/index";
import { processPageStructure, serializePageConfig } from "@/lib/utils/pageConfigLoader";
import { createLoadingComponent, createNoConfigComponent } from "@/components/ui/LoadingStates";

export interface UsePageConfigOptions {
    sectionKey: 'home' | 'text_management' | 'glossary' | 'blueprints' | 'typesandtags' | 'settings' | 'documents' | 'tms_page' | 'library';
  defaultConfig?: PageLayoutConfig;
  title?: string;
  dependencies?: any[];
  onConfigUpdate?: (config: PageLayoutConfig) => void;
  loadingMessage?: string;
  noConfigMessage?: string;
  handlers?: {
    onEdit?: (row: Record<string, unknown>) => void;
    onDuplicate?: (row: Record<string, unknown>) => void;
    onDelete?: (row: Record<string, unknown>) => void;
    onAddNew?: () => void;
    onCellUpdate?: (row: Record<string, unknown>, columnKey: string, newValue: string) => void;
    onCardClick?: (card: Record<string, unknown>) => void; // Add this line
  };
}

export interface UsePageConfigReturn {
  pageLayoutConfig: PageLayoutConfig | null;
  setPageLayoutConfig: (config: PageLayoutConfig) => void;
  isLoading: boolean;
  handlePageConfigChange: (newConfig: PageLayoutConfig) => Promise<void>;
  reloadConfig: () => void;
  LoadingComponent: React.ComponentType;
  NoConfigComponent: React.ComponentType;
}

export function usePageConfig({
  sectionKey,
  defaultConfig,
  title,
  dependencies = [],
  onConfigUpdate,
  loadingMessage = "Loading...",
  noConfigMessage = "No configuration available",
  handlers,
}: UsePageConfigOptions): UsePageConfigReturn {
  const { toast } = useToast();
  const { currentPageStructure, isLoading, updateSection } = usePageStructure();
  const [pageLayoutConfig, setPageLayoutConfig] = useState<PageLayoutConfig | null>(null);
  
  // Use a ref to track the latest handlers without causing re-renders
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // Memoize the onConfigUpdate callback to prevent infinite loops
  const memoizedOnConfigUpdate = useCallback((config: PageLayoutConfig) => {
    onConfigUpdate?.(config);
  }, [onConfigUpdate]);

  // Use a ref to track the last processed structure ID to prevent infinite loops
  const lastProcessedIdRef = useRef<string | null>(null);
  const lastProcessedSectionDataRef = useRef<string | null>(null);

  // Load page structure on mount and when dependencies change
  useEffect(() => {
    // Log the current state for debugging
    console.log(`[usePageConfig:${sectionKey}] State:`, {
      isLoading,
      hasStructure: !!currentPageStructure,
      structureId: currentPageStructure?.id,
      hasSection: !!(currentPageStructure?.[sectionKey]),
    });

    const structureId = currentPageStructure?.id || null;
    const sectionDataStr = currentPageStructure?.[sectionKey] 
      ? JSON.stringify(currentPageStructure[sectionKey]) 
      : null;
    
    // Only process if the structure or section data has actually changed
    if (lastProcessedIdRef.current === structureId && 
        lastProcessedSectionDataRef.current === sectionDataStr) {
      return;
    }
    
    lastProcessedIdRef.current = structureId;
    lastProcessedSectionDataRef.current = sectionDataStr;

    if (currentPageStructure?.[sectionKey]) {
      try {
        const sectionData = currentPageStructure[sectionKey] as Record<string, unknown>;
        if (sectionData && typeof sectionData === 'object' && 'rows' in sectionData) {
          const processedConfig = processPageStructure(sectionData, handlersRef.current);
          setPageLayoutConfig(processedConfig);
          memoizedOnConfigUpdate(processedConfig);
        }
      } catch (error) {
        console.error(`Error loading ${sectionKey} structure:`, error);
        
        // Always fall back to default config on error
        if (defaultConfig) {
          console.log(`Using default config for ${sectionKey} due to error`);
          setPageLayoutConfig(defaultConfig);
          memoizedOnConfigUpdate(defaultConfig);
        } else {
          // If no default config, show the error to user
          toast({
            title: "Error",
            description: `Failed to load ${sectionKey} structure`,
            variant: "destructive",
          });
        }
      }
    } else if (!isLoading) {
      // Loading is complete - apply default config if:
      // 1. Not currently loading
      // 2. Default config is provided
      // This handles both cases:
      //   - currentPageStructure exists but section is missing/null
      //   - currentPageStructure is null (no page structure found for user)
      if (defaultConfig) {
        console.log(`[usePageConfig:${sectionKey}] Using default config (structure: ${!!currentPageStructure}, section: ${!!(currentPageStructure?.[sectionKey])})`);
        setPageLayoutConfig(defaultConfig);
        memoizedOnConfigUpdate(defaultConfig);
        
        // Only save the default configuration to the API if the structure exists
        // but the section is truly missing
        if (currentPageStructure?.id && !(sectionKey in currentPageStructure)) {
          const serializedConfig = serializePageConfig(defaultConfig);
          updateSection(currentPageStructure.id, sectionKey, serializedConfig).catch((error) => {
            console.error(`Error saving default ${sectionKey} structure:`, error);
          });
        }
      }
    }
    // If isLoading is true, don't do anything yet
    // This prevents showing "No configuration" while data is still being fetched
  }, [currentPageStructure, isLoading, sectionKey, memoizedOnConfigUpdate, defaultConfig, updateSection, toast]);

  // Handle page config changes and save to API
  const handlePageConfigChange = useCallback(async (newConfig: PageLayoutConfig) => {
    console.log(`Page config changed for ${sectionKey}:`, newConfig);
    setPageLayoutConfig(newConfig);
    memoizedOnConfigUpdate(newConfig);

    // Update the page structure in the store using the more efficient updateSection function
    if (currentPageStructure?.id) {
      try {
        // Serialize the config to remove React elements and functions
        const serializedConfig = serializePageConfig(newConfig);
        await updateSection(currentPageStructure.id, sectionKey, serializedConfig);
        toast({
          title: "Success",
          description: `${title || sectionKey} structure saved`,
        });
      } catch (error) {
        console.error(`Error saving ${sectionKey} structure:`, error);
        toast({
          title: "Error",
          description: `Failed to save ${title || sectionKey} structure`,
          variant: "destructive",
        });
      }
    }
  }, [currentPageStructure?.id, sectionKey, title, updateSection, memoizedOnConfigUpdate, toast]);

  // Reload config from current page structure
  const reloadConfig = useCallback(() => {
    if (currentPageStructure?.[sectionKey]) {
      try {
        const sectionData = currentPageStructure[sectionKey] as Record<string, unknown>;
        if (sectionData && typeof sectionData === 'object' && 'rows' in sectionData) {
          const processedConfig = processPageStructure(sectionData, handlersRef.current);
          setPageLayoutConfig(processedConfig);
          memoizedOnConfigUpdate(processedConfig);
        }
      } catch (error) {
        console.error(`Error reloading ${sectionKey} structure:`, error);
        toast({
          title: "Error",
          description: `Failed to reload ${sectionKey} structure`,
          variant: "destructive",
        });
      }
    }
  }, [currentPageStructure, sectionKey, memoizedOnConfigUpdate, toast]);

  // Create components with the specific messages - memoized to prevent infinite loops
  const LoadingComponent = useMemo(() => createLoadingComponent(loadingMessage), [loadingMessage]);
  const NoConfigComponent = useMemo(() => createNoConfigComponent(noConfigMessage), [noConfigMessage]);

  return {
    pageLayoutConfig,
    setPageLayoutConfig,
    isLoading,
    handlePageConfigChange,
    reloadConfig,
    LoadingComponent,
    NoConfigComponent,
  };
} 